import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { initializePaddle } from '@paddle/paddle-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { getPlanLevel, getPlans } from '@/billing/plan';
import { useQueryClient } from '@tanstack/react-query';
import { createPaddleCheckout } from '@/services/billing';
export const Route = createFileRoute('/console/_authenticated/_layout/workspace/_layout/billing')({
    component: BillingPage,
});
const STATUS_STYLE = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    trialing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    paused: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    past_due: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    warning: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    grace: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    canceled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    expired: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
};
function SubscriptionStatusBadge({ status }) {
    const { t } = useTranslation('glossary');
    const label = t(`workspaces.billing.status.${status}`, { defaultValue: status });
    return (<span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[status] ?? STATUS_STYLE.active}`}>
            {label}
        </span>);
}
function BillingPage() {
    const { t } = useTranslation('glossary');
    const { workspace } = useAuthenticationState();
    const queryClient = useQueryClient();
    const [billing, setBilling] = React.useState('annual');
    const [paddle, setPaddle] = React.useState();
    React.useEffect(() => {
        const root = document.getElementById('adora_analytic_application');
        const environment = (root?.getAttribute('data-paddle-environment') ?? 'sandbox');
        const token = root?.getAttribute('data-paddle-client-token') ?? '';
        if (!token)
            return;
        initializePaddle({
            environment,
            token,
            eventCallback: (event) => {
                if (event.name === 'checkout.completed') {
                    toast.success(t('workspaces.billing.checkout_success'));
                    queryClient.invalidateQueries({ queryKey: ['my-workspaces'] });
                    queryClient.invalidateQueries({ queryKey: ['my-current-workspace'] });
                }
            },
        }).then(setPaddle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const onSubscribe = React.useCallback((target) => {
        if (!workspace || target === 'free')
            return;
        createPaddleCheckout({
            workspaceId: workspace.publicId,
            planCode: target,
            period: billing === 'annual' ? 'yearly' : 'monthly',
        })
            .then(({ priceId, customerId, customData }) => {
            paddle?.Checkout.open({
                items: [{ priceId, quantity: 1 }],
                customer: { id: customerId },
                customData,
                settings: { displayMode: 'overlay', theme: 'light' },
            });
        })
            .catch((err) => toast.error(err.message));
    }, [paddle, billing, workspace]);
    if (!workspace) {
        return (<div className="flex h-full items-center justify-center">
                <p className="text-sm text-muted-foreground">{t('workspaces.not_found')}</p>
            </div>);
    }
    const currentPlanCode = (workspace.subscription?.plan?.code ?? 'free');
    const currentPlanName = workspace.subscription?.plan?.name ?? 'Free';
    const currentStatus = workspace.subscription?.status ?? 'active';
    const currentLevel = getPlanLevel(currentPlanCode);
    const plans = getPlans(billing).map((p) => {
        const isCurrent = p.code === currentPlanCode;
        const targetLevel = getPlanLevel(p.code);
        const isUpgrade = targetLevel > currentLevel;
        const isDowngrade = targetLevel < currentLevel;
        const ctaLabel = isCurrent
            ? t('workspaces.billing.current')
            : isUpgrade
                ? t('workspaces.billing.subscribe')
                : isDowngrade
                    ? t('workspaces.billing.request_downgrade')
                    : t('workspaces.billing.change_plan');
        return { ...p, isCurrent, ctaLabel };
    });
    return (<div className="space-y-6">
            {/* Header */}
            <div className="space-y-1">
                <h1 className="text-xl font-semibold">{t('workspaces.billing.title')}</h1>
                <p className="text-sm text-muted-foreground">{t('workspaces.billing.subtitle')}</p>
            </div>

            {/* Current plan */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">{t('workspaces.billing.current_plan')}</CardTitle>
                    <CardDescription>{t('workspaces.billing.checkout_note')}</CardDescription>
                </CardHeader>

                <CardContent className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{currentPlanName}</p>
                            <SubscriptionStatusBadge status={currentStatus}/>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('workspaces.billing.workspace')} {workspace.name}
                        </p>
                    </div>
                </CardContent>
            </Card>

            <Separator />

            {/* Plans section with period toggle */}
            <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                    <h2 className="text-base font-semibold">{t('workspaces.billing.plans')}</h2>
                    <p className="text-sm text-muted-foreground">{t('workspaces.billing.plans_desc')}</p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <Label htmlFor="period-toggle" className="text-sm">
                        {t('workspaces.billing.monthly')}
                    </Label>
                    <Switch id="period-toggle" checked={billing === 'annual'} onCheckedChange={(checked) => setBilling(checked ? 'annual' : 'monthly')}/>
                    <Label htmlFor="period-toggle" className="text-sm">
                        {t('workspaces.billing.yearly')}{' '}
                        <span className="text-xs text-green-600 dark:text-green-400">
                            {t('workspaces.billing.yearly_save')}
                        </span>
                    </Label>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {plans.map((p) => (<Card key={p.code} className={p.highlighted ? 'border-primary ring-1 ring-primary/40' : undefined}>
                        <CardHeader>
                            <div className="flex items-start justify-between gap-2">
                                <div className="space-y-1">
                                    <CardTitle className="text-base">{p.name}</CardTitle>
                                    <CardDescription>{p.description}</CardDescription>
                                </div>
                                {p.highlighted && (<Badge variant="default">{t('workspaces.billing.recommended')}</Badge>)}
                            </div>
                        </CardHeader>

                        <CardContent className="flex h-full flex-col justify-between space-y-4">
                            <div>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-2xl font-bold">{p.price}</span>
                                    {p.priceHint && (<span className="text-xs text-muted-foreground">{p.priceHint}</span>)}
                                </div>
                                {p.annualNote && (<p className="text-xs text-green-600 dark:text-green-400">{p.annualNote}</p>)}

                                <ul className="space-y-2 text-sm text-muted-foreground">
                                    {p.items.map((item) => (<li key={item}>• {item}</li>))}
                                </ul>
                            </div>

                            <Button className="mt-auto w-full" variant={p.isCurrent
                ? 'outline'
                : getPlanLevel(p.code) > currentLevel
                    ? 'default'
                    : 'secondary'} disabled={p.isCurrent} onClick={() => onSubscribe(p.code)}>
                                {p.ctaLabel}
                            </Button>
                        </CardContent>
                    </Card>))}
            </div>
        </div>);
}
